/**
 * App-Katalog (Web-Host) — kanonische Metadaten-Sammlung, parallel zum bestehenden Verhalten.
 *
 * Nächste Schritte (ohne Big Bang):
 * - apps/web/src/modules/app-loader.js → APP_REGISTRY / resolveAppKeyForRoute aus Katalog ableiten
 * - apps/web/src/reactApp.jsx → __appKeyForRoute / Guards aus derive/resolveRoute.js
 * - apps/web/src/pages/hub/GlobalHubPage.jsx → APP_CARDS aus getHubMarketplaceEntries()
 * - Marketplace-Detail (nur Metadaten): Route `/hub/apps/:appKey` → HubMarketplaceAppDetailPage; Links via buildMarketplaceAppDetailHref (href.js)
 * - apps/web/src/components/hub/AppCard.jsx → HUB_APP_KEYS aus hubAccentEligible / Katalog
 * - apps/web/src/components/hub/hubIcons.js → iconKey-Mapping
 * - apps/web/src/utils/governance/canAccessApp.js — ALL_APP_KEYS_PHASE1 via getAccessControlledAppKeys / inAllAppKeysPhase1
 * - packages/ui-components breadcrumb.registry.js → manifestAppId / routes mit Katalog abgleichen (optional Merge)
 * - apps/web/src/components/hub/RecentActivityList.jsx — getRecentActivityAppKeys / recentActivityEligible
 */

import { CATALOG_ENTRIES_STATIC, DOCUMENTED_NON_CATALOG_ROUTES } from "./catalog.static.js";
import {
  resolveAppKeyFromRoute,
  resolveLoaderAppKeyFromRoute,
  getFeatureFlagKeyForRoute as getFeatureFlagKeyForRouteInner,
} from "./derive/resolveRoute.js";

/** @type {ReadonlyMap<string, object>} */
const __byKey = new Map(CATALOG_ENTRIES_STATIC.map((e) => [e.appKey, e]));

/**
 * Alle statischen Katalog-Einträge (inkl. shellRoute/share/settings).
 */
export const appCatalog = Object.freeze(CATALOG_ENTRIES_STATIC);

/**
 * Dokumentierte Routen außerhalb des Marketplace-Katalogs (Governance/Admin).
 */
export const documentedNonCatalogRoutes = DOCUMENTED_NON_CATALOG_ROUTES;

export { CATALOG_ENTRIES_STATIC };

export { resolveAppKeyFromRoute, resolveLoaderAppKeyFromRoute };

/**
 * Vorbereitete, kompatible Manifest-Sicht pro App (ohne UI-Nutzung).
 * Behält bestehende Felder bei und ergänzt strukturierte shell/marketplace-Metadaten.
 * @returns {ReadonlyArray<Readonly<object>>}
 */
export function getAppManifestEntries() {
  return CATALOG_ENTRIES_STATIC;
}

export {
  deriveMarketplaceUiUsageFromCatalog,
  deriveMarketplaceUiUsageWhenListedWithoutAccess,
} from "./derive/marketplaceUiUsage.js";

/**
 * @param {string} appKey
 * @returns {Readonly<object>|null}
 */
export function getAppCatalogEntry(appKey) {
  const k = String(appKey || "").trim();
  if (!k) return null;
  return __byKey.get(k) ?? null;
}

/**
 * i18n-Key für Hub-App-Titel (Recent Activity, Breadcrumbs).
 * @param {string} appKey
 * @returns {string}
 */
export function getHubAppTitleKey(appKey) {
  const k = String(appKey || "").trim().toLowerCase();
  if (!k) return "";
  const entry =
    getAppCatalogEntry(k) ||
    (k === "schleifchen" ? getAppCatalogEntry("matchmixer") : null) ||
    (k === "matchmixer" ? getAppCatalogEntry("matchmixer") : null);
  return entry?.titleKey || `hub.${k}.title`;
}

const __catalogFacade = {
  getEntry: getAppCatalogEntry,
};

/**
 * Hub-Kacheln wie GlobalHubPage APP_CARDS (Reihenfolge, nur showOnGlobalHub).
 * @returns {ReadonlyArray<Readonly<object>>}
 */
export function getHubMarketplaceEntries() {
  return CATALOG_ENTRIES_STATIC.filter(
    (e) => e.showOnGlobalHub && !e.shellRoute && e.status !== "hidden"
  );
}

/**
 * appKeys mit Hub-Akzent (--app-{appKey}), wie AppCard.jsx HUB_APP_KEYS (hubAccentEligible im Katalog).
 * @returns {string[]}
 */
export function getHubAccentEligibleAppKeys() {
  return CATALOG_ENTRIES_STATIC.filter((e) => e.hubAccentEligible === true).map((e) => e.appKey);
}

/**
 * App-Keys für RecentActivityList (Akzent + inferAppKeyFromHref): `recentActivityEligible === true`.
 * IST: nur schleifchen, trainerstunden, clubmeisterschaft, medenspiele (ohne tennis-live/minigames).
 */
export const RECENT_ACTIVITY_APP_KEYS = Object.freeze(
  CATALOG_ENTRIES_STATIC.filter((e) => e.recentActivityEligible === true).map((e) => e.appKey)
);

/**
 * @returns {readonly string[]} Gleiche Liste wie historisch RecentActivityList HUB_APP_KEYS.
 */
export function getRecentActivityAppKeys() {
  return RECENT_ACTIVITY_APP_KEYS;
}

/**
 * Phase-1 Governance-„Vollpalette“ / Admin-Superadmin-Fallback (historisch ALL_APP_KEYS_PHASE1).
 * Ableitung: `inAllAppKeysPhase1 === true` — ausgeschlossen u.a. share, settings (im Katalog false).
 */
export const ACCESS_CONTROLLED_APP_KEYS_PHASE1 = Object.freeze(
  CATALOG_ENTRIES_STATIC.filter((e) => e.inAllAppKeysPhase1 === true).map((e) => e.appKey)
);

/**
 * @returns {readonly string[]} Gleiche Keys und Reihenfolge wie früher ALL_APP_KEYS_PHASE1 (schleifchen … tennis-live).
 */
export function getAccessControlledAppKeys() {
  return ACCESS_CONTROLLED_APP_KEYS_PHASE1;
}

/**
 * Feature-Flag-Key für eine Route (entspricht IST: Key aus __appKeyForRoute).
 * @param {string} routeOrPath
 * @returns {string|null}
 */
export function getFeatureFlagKeyForRoute(routeOrPath) {
  return getFeatureFlagKeyForRouteInner(routeOrPath, __catalogFacade);
}
